import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScript from '../hooks/useScript';
import { createBindCardTransaction } from '../services/api';

const JQUERY_URL = 'https://code.jquery.com/jquery-3.5.1.min.js';
const FORGE_URL = 'https://cdn.jsdelivr.net/npm/node-forge@0.7.0/dist/forge.min.js';
const ECPAY_SDK_URL = 'https://ecpg.ecpay.com.tw/Scripts/sdk-1.0.0.js?t=20210121100116';

const NAME_VALIDATION_REGEX = /^[\u4e00-\u9fa5a-zA-Z:.,.()/–-]+$/;
// 【新增】電話號碼的驗證規則 (只允許數字)
const PHONE_VALIDATION_REGEX = /^[0-9]+$/;

const CreditCardForm = ({ bindToken, transactionId }) => {
    const navigate = useNavigate();
    const [isSdkReady, setIsSdkReady] = useState(false);
    
    const [formError, setFormError] = useState('');
    const [nameError, setNameError] = useState('');
    const [phoneError, setPhoneError] = useState(''); // 【新增】電話的專屬錯誤狀態

    const [userInfo, setUserInfo] = useState({
        name: '',
        phone: '',
        address: '',
    });

    const jqueryStatus = useScript(JQUERY_URL);
    const forgeStatus = useScript(jqueryStatus === 'ready' ? FORGE_URL : null);
    const ecpaySdkStatus = useScript(forgeStatus === 'ready' ? ECPAY_SDK_URL : null);

    useEffect(() => {
        if (ecpaySdkStatus === 'ready' && bindToken && window.ECPay) {
            try {
                window.ECPay.initialize('Prod', 1, function (errMsg) {
                    if (errMsg) { setFormError(`ECPay Initialize Error: ${errMsg}`); return; }
                    window.ECPay.addBindingCard(bindToken, window.ECPay.Language.zhTW, function (errMsg) {
                        if (errMsg) { setFormError(`ECPay AddBindingCard Error: ${errMsg}`); } 
                        else { setIsSdkReady(true); }
                    }, 'V2');
                });
            } catch (err) {
                setFormError(`ECPay SDK Execution Error: ${err.message}`);
            }
        }
    }, [ecpaySdkStatus, bindToken]);

    const handleUserInfoChange = (e) => {
        const { name, value } = e.target;
        setUserInfo(prevState => ({ ...prevState, [name]: value }));

        // 姓名欄位驗證
        if (name === 'name') {
            if (value && !NAME_VALIDATION_REGEX.test(value)) {
                setNameError('姓名只能包含中、英文及 : , . ( ) / – - 符號 (不允許空格)');
            } else {
                setNameError('');
            }
        }

        // 【新增】電話欄位驗證
        if (name === 'phone') {
            if (value && !PHONE_VALIDATION_REGEX.test(value)) {
                setPhoneError('電話號碼只能包含數字');
            } else {
                setPhoneError('');
            }
        }
    };

    const handlePay = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!userInfo.name || !userInfo.phone || !userInfo.address) {
            setFormError('請填寫所有個人資訊欄位。');
            return;
        }
        
        // 【新增】送出前再次檢查姓名和電話的錯誤
        if (nameError || phoneError) {
            setFormError('請修正欄位中的錯誤。');
            return;
        }
        
        if (!isSdkReady || !window.ECPay) {
            setFormError('SDK尚未準備完成。');
            return;
        }

        try {
            window.ECPay.getBindCardPayToken(async (paymentInfo, errMsg) => {
                if (errMsg || !paymentInfo.BindCardPayToken) {
                    setFormError(`取得 PayToken 失敗: ${errMsg}`);
                    return;
                }
                
                const payload = {
                    bindCardPayToken: paymentInfo.BindCardPayToken,
                    transactionId: transactionId,
                    creditCardInfo: { 
                        name: userInfo.name,
                        phone: userInfo.phone,
                        countryCode: '183',
                        address: userInfo.address
                    }
                };

                const response = await createBindCardTransaction(payload);
                
                if (response.data.data.threeDUrl) {
                    alert('即將開啟 3D 驗證分頁。完成驗證後，請回到本站並手動刷新信用卡列表。');
                    window.open(response.data.data.threeDUrl, '_blank');
                } else {
                    alert('綁定請求已送出，請稍後手動刷新信用卡列表查看結果。');
                }
                
                navigate('/manage-cards');
            });
        } catch (err) {
            setFormError(`交易錯誤: ${err.response?.data?.message || err.message}`);
        }
    };

    const getLoadingMessage = () => {
        if (jqueryStatus !== 'ready') return '正在載入 jQuery...';
        if (forgeStatus !== 'ready') return '正在載入加密套件 (Forge)...';
        if (ecpaySdkStatus !== 'ready') return '正在載入 ECPay 付款元件...';
        return null;
    };

    return (
        <div className="payment-container">
            <h3>綁定新信用卡</h3>
            {getLoadingMessage() && <p>{getLoadingMessage()}</p>}
            <div id="ECPayPayment"></div>
            <div className="test-card-info">
                <h4>測試信用卡資訊</h4>
                <p>卡號: 4311-9522-2222-2222</p>
                <p>安全碼: 222</p>
            </div>
            <hr className="divider" />
            <form onSubmit={handlePay} className="user-info-form">
                <h4>持卡人資訊</h4>
                <div className="form-group">
                    <label htmlFor="name">姓名</label>
                    <input 
                        type="text" 
                        id="name" 
                        name="name"
                        value={userInfo.name}
                        onChange={handleUserInfoChange}
                        placeholder="請輸入持卡人姓名"
                        className={nameError ? 'input-has-error' : ''}
                    />
                    {nameError && <p className="input-error-message">{nameError}</p>}
                </div>
                <div className="form-group">
                    <label htmlFor="phone">電話</label>
                    <input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        value={userInfo.phone} 
                        onChange={handleUserInfoChange} 
                        placeholder="請輸入聯絡電話"
                        className={phoneError ? 'input-has-error' : ''} // 【新增】
                    />
                    {phoneError && <p className="input-error-message">{phoneError}</p>} {/* 【新增】 */}
                </div>
                <div className="form-group">
                    <label htmlFor="address">地址</label>
                    <input type="text" id="address" name="address" value={userInfo.address} onChange={handleUserInfoChange} placeholder="請輸入完整地址" />
                </div>
                {formError && <p className="error-message">{formError}</p>}
                <button type="submit" disabled={!isSdkReady} className="pay-button">確認送出</button>
            </form>
        </div>
    );
};

export default CreditCardForm;